import { useState, useEffect, useCallback } from 'react';
import { Heart, Repeat2, MessageCircle, ShieldCheck, DollarSign, Bookmark, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import ShareModal from './ShareModal';
import QuickDonateModal from './QuickDonateModal';
import VerifyPopover from './VerifyPopover';
import CommentSection from './CommentSection';
import { getStore, setStore } from '@/utils/localStore';

const REACTIONS = [
  { key: 'celebrates', icon: Heart, label: 'Celebrate' },
  { key: 'amplifies', icon: Repeat2, label: 'Amplify' },
  { key: 'discusses', icon: MessageCircle, label: 'Discuss' },
  { key: 'verifies', icon: ShieldCheck, label: 'Verify' },
  { key: 'funds', icon: DollarSign, label: 'Fund' },
];

const MENU_OPTIONS = [
  { key: 'save', label: 'Save post' },
  { key: 'copy', label: 'Copy link' },
  { key: 'not_interested', label: 'Not interested' },
  { key: 'report', label: 'Report' },
];

function CardActions({ bookmarked, onBookmark, showMenu, onToggleMenu, onMenuAction, postId }) {
  return (
    <div className="absolute -top-12 right-0 flex items-center gap-1">
      <button onClick={onBookmark} className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${bookmarked ? 'text-[#7C3AED]' : 'text-slate-300 hover:text-slate-500'}`} data-testid={`bookmark-${postId}`}>
        <Bookmark className={`w-4 h-4 ${bookmarked ? 'fill-current' : ''}`} />
      </button>
      <div className="relative">
        <button onClick={onToggleMenu} className="w-7 h-7 rounded-md flex items-center justify-center text-slate-300 hover:text-slate-500" data-testid={`menu-${postId}`}>
          <MoreHorizontal className="w-4 h-4" />
        </button>
        {showMenu && (
          <div className="absolute top-full right-0 mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-40 py-1" data-testid={`menu-dropdown-${postId}`}>
            {MENU_OPTIONS.map(opt => (
              <button key={opt.key} onClick={() => onMenuAction(opt.key)} className="w-full text-left px-3 py-2 text-xs text-slate-600 hover:bg-slate-50">{opt.label}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReactionButtons({ reactions, counts, toggled, showComments, showVerify, onToggle, postId }) {
  return (
    <div className="flex items-center gap-1 pt-3 border-t border-slate-100">
      {reactions.map(r => {
        const count = counts[r.key] || 0;
        const active = toggled[r.key] || (r.key === 'discusses' && showComments) || (r.key === 'verifies' && showVerify);
        return (
          <button
            key={r.key}
            onClick={() => onToggle(r.key)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${active ? 'text-[#7C3AED] bg-[#7C3AED]/5' : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'}`}
            data-testid={`reaction-${r.key}-${postId}`}
          >
            <r.icon className={`w-3.5 h-3.5 ${active && r.key === 'celebrates' ? 'fill-current text-rose-500' : ''}`} />
            {r.key === 'funds' && count > 0 ? <span>${count.toLocaleString()}</span> : count > 0 ? <span>{count}</span> : <span className="hidden sm:inline">{r.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default function ReactionBar({ data = {}, postId, txHash, projectTitle, projectSlug, commentsCount = 0 }) {
  const { user } = useAuth();
  const userId = user?.username || 'anonymous';
  const [toggled, setToggled] = useState({});
  const [counts, setCounts] = useState(() => ({
    ...data,
    discusses: data.discusses || commentsCount || 0,
  }));
  const [showComments, setShowComments] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showDonate, setShowDonate] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handleToggle = useCallback((key) => {
    if (key === 'amplifies') { setShowShare(true); return; }
    if (key === 'discusses') { setShowComments(prev => !prev); return; }
    if (key === 'verifies') { setShowVerify(prev => !prev); return; }
    if (key === 'funds') { setShowDonate(true); return; }

    // Optimistic toggle
    setToggled(prev => {
      const next = { ...prev, [key]: !prev[key] };
      setCounts(c => ({ ...c, [key]: next[key] ? (c[key] || 0) + 1 : Math.max(0, (c[key] || 0) - 1) }));
      return next;
    });

    // Persist toggle to localStorage
    const reactions = getStore('reactions');
    const idx = reactions.findIndex(r => r.postId === postId && r.userId === userId && r.reactionType === key);
    if (idx >= 0) {
      reactions.splice(idx, 1);
    } else {
      reactions.push({ postId, userId, reactionType: key, createdAt: new Date().toISOString() });
    }
    setStore('reactions', reactions);
  }, [postId, userId]);

  const handleCommentAdd = () => setCounts(c => ({ ...c, discusses: (c.discusses || 0) + 1 }));

  const handleBookmark = useCallback(() => {
    setBookmarked(prev => !prev);
    toast.success(bookmarked ? 'Removed from saved' : 'Saved!');

    // Persist toggle to localStorage
    const bookmarks = getStore('bookmarks');
    const idx = bookmarks.findIndex(b => b.userId === userId && b.postId === postId);
    if (idx >= 0) {
      bookmarks.splice(idx, 1);
    } else {
      bookmarks.push({ userId, postId, createdAt: new Date().toISOString() });
    }
    setStore('bookmarks', bookmarks);
  }, [bookmarked, postId, userId]);

  const handleMenuAction = (action) => {
    setShowMenu(false);
    const messages = { save: 'Saved!', copy: 'Link copied!', not_interested: "Got it, we'll show less like this", report: 'Reported. Thanks for letting us know.' };
    toast.success(messages[action]);
  };

  return (
    <div className="relative">
      <CardActions
        bookmarked={bookmarked}
        onBookmark={handleBookmark}
        showMenu={showMenu}
        onToggleMenu={() => setShowMenu(prev => !prev)}
        onMenuAction={handleMenuAction}
        postId={postId}
      />
      <ReactionButtons
        reactions={REACTIONS}
        counts={counts}
        toggled={toggled}
        showComments={showComments}
        showVerify={showVerify}
        onToggle={handleToggle}
        postId={postId}
      />
      {showVerify && <VerifyPopover open={showVerify} onClose={() => setShowVerify(false)} txHash={txHash} />}
      <CommentSection postId={postId} commentsCount={commentsCount} expanded={showComments} onCommentAdd={handleCommentAdd} />
      <ShareModal open={showShare} onClose={() => setShowShare(false)} postId={postId} />
      <QuickDonateModal open={showDonate} onClose={() => setShowDonate(false)} projectTitle={projectTitle} projectSlug={projectSlug} />
    </div>
  );
}
